import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { API, authHeaders, jsonAuthHeaders } from "../../services/config";
import "./Odontograma.css";

// ── Colores fijos con semántica clínica ─────────────────────────────────────
const C = {
  extraccion: "#1d4ed8", // X azul = extracción a realizar
  ausente:    "#D32F2F", // X roja = diente ausente
  corona:     "#7c3aed", // ○ morado = corona
  bridge:     "#d97706", // barras ámbar = puente (pilar y póntico)
  caries:     "#64B5F6", // cuadrante azul = caries a tratar
  restaurado: "#ef4444", // cuadrante rojo = restaurado
  endodoncia: "#f97316", // naranja
  implante:   "#6366f1", // índigo (metálico, distinto del verde)
  sellador:   "#eab308", // ámbar/amarillo (resina, distinto del índigo)
  fractura:   "#374151", // gris oscuro
  otro:       "#BDBDBD", // gris medio
};

// ── Acciones para diente completo ────────────────────────────────────────────
const ACCIONES_DIENTE = [
  { key: "extraccion",     label: "Extracción", icon: "✕", color: C.extraccion, hint: "X azul"   },
  { key: "ausente",        label: "Ausente",    icon: "✕", color: C.ausente,    hint: "X roja"   },
  { key: "corona",         label: "Corona",     icon: "○", color: C.corona,     hint: "Círculo"  },
  { key: "puente_pilar",   label: "P. Pilar",   icon: "⌀", color: C.bridge,     hint: "Pilar"    },
  { key: "puente_pontico", label: "Póntico",    icon: "⊟", color: C.bridge,     hint: "Póntico"  },
  { key: "limpiar",        label: "Limpiar",    icon: "↺", color: "#757575",    hint: "Borra"    },
];

// ── Acciones para cuadrante (cara) ───────────────────────────────────────────
const ACCIONES_CARA = [
  { key: "caries",     label: "Caries",     color: C.caries     },
  { key: "restauracion", label: "Restaurado", color: C.restaurado },
  { key: "endodoncia", label: "Endodoncia", color: C.endodoncia },
  { key: "implante",   label: "Implante",   color: C.implante   },
  { key: "sellador",   label: "Sellador",   color: C.sellador   },
  { key: "fractura",   label: "Fractura",   color: C.fractura   },
  { key: "otro",       label: "Otro",       color: C.otro       },
];

const ALL_ACCIONES = [...ACCIONES_DIENTE, ...ACCIONES_CARA];
const WHOLE_KEYS   = new Set(["extraccion", "ausente", "corona", "puente_pilar", "puente_pontico", "limpiar"]);
const FACES        = ["oclusal", "mesial", "distal", "vestibular", "lingual"];
const EMPTY_FACE   = { hallazgo: null, color: null };

const TOOTH_NUMBERS = [
  18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28,
  55, 54, 53, 52, 51,                 61, 62, 63, 64, 65,
  48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38,
  85, 84, 83, 82, 81,                 71, 72, 73, 74, 75,
];

function createInitialState() {
  return TOOTH_NUMBERS.reduce((acc, n) => {
    acc[n] = {
      oclusal:    { ...EMPTY_FACE },
      mesial:     { ...EMPTY_FACE },
      distal:     { ...EMPTY_FACE },
      vestibular: { ...EMPTY_FACE },
      lingual:    { ...EMPTY_FACE },
      cross:  null, // color string: C.extraccion o C.ausente
      circle: null, // color string: C.corona
      bridge: null, // null | "pilar" | "pontico"
    };
    return acc;
  }, {});
}

function mapApiToState(piezas) {
  const next = createInitialState();
  (piezas || []).forEach((row) => {
    if (!next[row.pieza]) return;
    if (row.marca === "cross") {
      next[row.pieza].cross = row.hallazgo === "extraccion" || row.color === C.extraccion
        ? C.extraccion
        : C.ausente;
    }
    if (row.marca === "circle")         next[row.pieza].circle = row.color;
    if (row.marca === "bridge_pilar")   next[row.pieza].bridge = "pilar";
    if (row.marca === "bridge_pontico") next[row.pieza].bridge = "pontico";
    if (FACES.includes(row.cara)) {
      next[row.pieza][row.cara] = { hallazgo: row.hallazgo || "otro", color: row.color };
    }
  });
  return next;
}

function mapStateToPayload(dientes, observaciones) {
  const piezas = [];
  Object.entries(dientes).forEach(([pieza, data]) => {
    const p = parseInt(pieza, 10);
    FACES.forEach((face) => {
      if (data[face].color) {
        piezas.push({ pieza: p, cara: face, hallazgo: data[face].hallazgo || "otro", color: data[face].color });
      }
    });
    if (data.cross) {
      piezas.push({
        pieza: p, cara: "completa", marca: "cross",
        hallazgo: data.cross === C.extraccion ? "extraccion" : "ausente",
        color: data.cross,
      });
    }
    if (data.circle) {
      piezas.push({ pieza: p, cara: "completa", marca: "circle", hallazgo: "corona", color: data.circle });
    }
    if (data.bridge === "pilar") {
      piezas.push({ pieza: p, cara: "completa", marca: "bridge_pilar",   hallazgo: "puente_pilar",   color: C.bridge });
    }
    if (data.bridge === "pontico") {
      piezas.push({ pieza: p, cara: "completa", marca: "bridge_pontico", hallazgo: "puente_pontico", color: C.bridge });
    }
  });
  return { observaciones, piezas };
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function Odontograma({ pacienteId, embedded = false, onClose, onSaved }) {
  const { id: routeId } = useParams();
  const id = pacienteId || routeId;

  const [selectedAction, setSelectedAction] = useState("caries");
  const [showPermanentes, setShowPermanentes] = useState(true);
  const [showTemporarios, setShowTemporarios] = useState(true);
  const [dientes, setDientes] = useState(createInitialState);
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");
  const [dirty,    setDirty]    = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [observaciones, setObservaciones] = useState("");

  const loadOdontograma = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const res  = await fetch(`${API}/pacientes/${id}/odontograma`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error("No se pudo cargar el odontograma");
      setDientes(mapApiToState(data.data?.piezas || []));
      setMetadata(data.data?.odontograma || null);
      setObservaciones(data.data?.odontograma?.observaciones || "");
      setDirty(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadOdontograma(); }, [loadOdontograma]);

  // ── Aplicar marca a diente completo ──────────────────────────────────────
  const applyWholeTooth = useCallback((num, action) => {
    setDientes((prev) => {
      const t = prev[num];
      switch (action) {
        case "extraccion":
          return { ...prev, [num]: { ...t, cross:  t.cross  === C.extraccion ? null : C.extraccion } };
        case "ausente":
          return { ...prev, [num]: { ...t, cross:  t.cross  === C.ausente    ? null : C.ausente    } };
        case "corona":
          return { ...prev, [num]: { ...t, circle: t.circle ? null : C.corona } };
        case "puente_pilar":
          return { ...prev, [num]: { ...t, bridge: t.bridge === "pilar"   ? null : "pilar"   } };
        case "puente_pontico":
          return { ...prev, [num]: { ...t, bridge: t.bridge === "pontico" ? null : "pontico" } };
        case "limpiar":
          return {
            ...prev,
            [num]: {
              oclusal: { ...EMPTY_FACE }, mesial:  { ...EMPTY_FACE },
              distal:  { ...EMPTY_FACE }, vestibular: { ...EMPTY_FACE },
              lingual: { ...EMPTY_FACE }, cross: null, circle: null, bridge: null,
            },
          };
        default: return prev;
      }
    });
    setDirty(true);
  }, []);

  // ── Aplicar color a una cara ──────────────────────────────────────────────
  const applyFace = useCallback((num, face, action) => {
    const accion = ACCIONES_CARA.find((a) => a.key === action);
    if (!accion) return;
    setDientes((prev) => {
      const current = prev[num][face];
      const isSame  = current.color === accion.color;
      return {
        ...prev,
        [num]: { ...prev[num], [face]: isSame ? { ...EMPTY_FACE } : { hallazgo: accion.key, color: accion.color } },
      };
    });
    setDirty(true);
  }, []);

  // ── Handler unificado de click en tooth-square ────────────────────────────
  const handleToothClick = useCallback((num, e) => {
    if (WHOLE_KEYS.has(selectedAction)) {
      applyWholeTooth(num, selectedAction);
      return;
    }
    const faceEl = e.target.closest("[data-face]");
    if (faceEl) applyFace(num, faceEl.dataset.face, selectedAction);
  }, [selectedAction, applyWholeTooth, applyFace]);

  // ── Guardar ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = mapStateToPayload(dientes, observaciones);
      const res  = await fetch(`${API}/pacientes/${id}/odontograma`, {
        method: "PUT",
        headers: jsonAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data?.message || "No se pudo guardar");
      setDirty(false);
      setMetadata(data.data?.odontograma || null);
      if (typeof onSaved === "function") onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Resumen de marcas ─────────────────────────────────────────────────────
  const summary = useMemo(() => {
    let n = 0;
    Object.values(dientes).forEach((t) => {
      FACES.forEach((f) => { if (t[f].color) n++; });
      if (t.cross)  n++;
      if (t.circle) n++;
      if (t.bridge) n++;
    });
    return n;
  }, [dientes]);

  // ── Renderizar fila de dientes con contexto de puente ────────────────────
  const renderToothRow = (nums) =>
    nums.map((num, i) => {
      const t           = dientes[num];
      const bridgeLeft  = i > 0            && !!dientes[nums[i - 1]]?.bridge;
      const bridgeRight = i < nums.length - 1 && !!dientes[nums[i + 1]]?.bridge;
      const hasBridge   = !!t.bridge;

      const tooltipParts = [
        t.cross  === C.extraccion ? "Extracción" : t.cross === C.ausente ? "Ausente" : null,
        t.circle ? "Corona" : null,
        t.bridge === "pilar"   ? "Pilar puente" : null,
        t.bridge === "pontico" ? "Póntico" : null,
        ...FACES.filter((f) => t[f].hallazgo).map((f) => `${f}: ${t[f].hallazgo}`),
      ].filter(Boolean).join(" · ");

      return (
        <div
          key={num}
          className={[
            "tooth",
            hasBridge   ? `bridge-${t.bridge}` : "",
            bridgeLeft  ? "bridge-connect-left"  : "",
            bridgeRight ? "bridge-connect-right" : "",
          ].filter(Boolean).join(" ")}
          title={tooltipParts || String(num)}
        >
          <span className="tooth-number">{num}</span>
          <div
            className="tooth-square"
            onClick={(e) => handleToothClick(num, e)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleToothClick(num, e);
            }}
            aria-label={`Diente ${num}${tooltipParts ? `: ${tooltipParts}` : ""}`}
          >
            <div className="face vestibular" data-face="vestibular" style={{ background: t.vestibular.color || undefined }} />
            <div className="face mesial"     data-face="mesial"     style={{ background: t.mesial.color    || undefined }} />
            <div className="face oclusal"    data-face="oclusal"    style={{ background: t.oclusal.color   || undefined }} />
            <div className="face distal"     data-face="distal"     style={{ background: t.distal.color    || undefined }} />
            <div className="face lingual"    data-face="lingual"    style={{ background: t.lingual.color   || undefined }} />

            {/* X: extracción (azul) o ausente (roja) */}
            {t.cross && (
              <div className="cross-overlay" style={{ color: t.cross }}>✕</div>
            )}

            {/* Círculo: corona */}
            {t.circle && (
              <div className="circle-overlay" style={{ borderColor: t.circle }} />
            )}

            {/* Puente: barras ámbar arriba y abajo */}
            {hasBridge && (
              <>
                <div className="bridge-bar bridge-bar-top" />
                <div className="bridge-bar bridge-bar-bottom" />
              </>
            )}
          </div>
        </div>
      );
    });

  const currentAccion = ALL_ACCIONES.find((a) => a.key === selectedAction);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={embedded ? "odonto-embedded" : "odonto-modal"}>
      <div className="odonto-content">

        {/* Header */}
        <div className="odonto-header">
          <h2 className="odonto-title">Odontograma</h2>
          {dirty && <span className="odonto-dirty-badge">● Sin guardar</span>}
        </div>

        {/* Selector compacto para mobile: evita que la paleta ocupe media pantalla */}
        <div className="odonto-mobile-tool">
          <label className="odonto-mobile-tool__label" htmlFor="odonto-mobile-action">
            Herramienta
          </label>
          <div className="odonto-mobile-tool__control">
            <span className="odonto-mobile-tool__preview" style={{ background: currentAccion?.color }}>
              {currentAccion?.icon || ""}
            </span>
            <select
              id="odonto-mobile-action"
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              aria-label="Seleccionar herramienta del odontograma"
            >
              <optgroup label="Diente completo">
                {ACCIONES_DIENTE.map((a) => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
              </optgroup>
              <optgroup label="Cuadrante">
                {ACCIONES_CARA.map((a) => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
              </optgroup>
            </select>
          </div>
        </div>

        {/* Paleta de herramientas */}
        <div className="odonto-palette">
          <div className="odonto-palette-section">
            <span className="palette-section-label">Diente completo</span>
            <div className="palette-chips">
              {ACCIONES_DIENTE.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  className={`palette-chip${selectedAction === a.key ? " palette-chip--active" : ""}`}
                  style={{ "--chip-color": a.color }}
                  onClick={() => setSelectedAction(a.key)}
                  title={`${a.label}: ${a.hint}`}
                >
                  <span className="chip-icon" style={{ color: a.color }}>{a.icon}</span>
                  <span className="chip-label">{a.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="odonto-palette-section">
            <span className="palette-section-label">Cuadrante</span>
            <div className="palette-chips">
              {ACCIONES_CARA.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  className={`palette-chip${selectedAction === a.key ? " palette-chip--active" : ""}`}
                  style={{ "--chip-color": a.color }}
                  onClick={() => setSelectedAction(a.key)}
                >
                  <span className="chip-dot" style={{ background: a.color }} />
                  <span className="chip-label">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Barra de modo activo + toggles */}
        <div className="odonto-controls-bar">
          <div className="odonto-active-mode">
            <span className="active-mode-dot" style={{ background: currentAccion?.color }}>
              {currentAccion?.icon || "●"}
            </span>
            <span className="active-mode-text">
              <strong>{currentAccion?.label}</strong>
              {" — "}
              {selectedAction === "limpiar"
                ? "tocá el diente para borrarlo"
                : WHOLE_KEYS.has(selectedAction)
                  ? "tocá el diente"
                  : "tocá el cuadrante"}
            </span>
          </div>
          <div className="odonto-toggle-row">
            <button
              type="button"
              className={`odonto-toggle-btn${showPermanentes ? " odonto-toggle-btn--on" : ""}`}
              onClick={() => setShowPermanentes((v) => !v)}
            >
              Permanentes
            </button>
            <button
              type="button"
              className={`odonto-toggle-btn${showTemporarios ? " odonto-toggle-btn--on" : ""}`}
              onClick={() => setShowTemporarios((v) => !v)}
            >
              Temporarios
            </button>
          </div>
        </div>

        {/* Grilla de dientes */}
        {loading ? (
          <div className="odonto-loading">Cargando odontograma…</div>
        ) : (
          <div className="odonto-scroll-wrap">
            <div className="odonto-layout">
              {showPermanentes && (
                <>
                  <div className="odonto-arch-label">Superior permanente</div>
                  <div className="row">
                    {renderToothRow([18, 17, 16, 15, 14, 13, 12, 11])}
                    <div className="separator" />
                    {renderToothRow([21, 22, 23, 24, 25, 26, 27, 28])}
                  </div>
                </>
              )}
              {showTemporarios && (
                <div className="row temporario-row">
                  {renderToothRow([55, 54, 53, 52, 51])}
                  <div className="separator" />
                  {renderToothRow([61, 62, 63, 64, 65])}
                </div>
              )}

              <div className="odonto-midline" />

              {showTemporarios && (
                <div className="row temporario-row">
                  {renderToothRow([85, 84, 83, 82, 81])}
                  <div className="separator" />
                  {renderToothRow([71, 72, 73, 74, 75])}
                </div>
              )}
              {showPermanentes && (
                <>
                  <div className="row">
                    {renderToothRow([48, 47, 46, 45, 44, 43, 42, 41])}
                    <div className="separator" />
                    {renderToothRow([31, 32, 33, 34, 35, 36, 37, 38])}
                  </div>
                  <div className="odonto-arch-label">Inferior permanente</div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Leyenda */}
        <details className="odonto-leyenda">
          <summary className="odonto-leyenda-summary">Leyenda de colores</summary>
          <div className="leyenda-grid">
            <div className="leyenda-item">
              <span className="leyenda-x" style={{ color: C.extraccion }}>✕</span>
              <span>Extracción</span>
            </div>
            <div className="leyenda-item">
              <span className="leyenda-x" style={{ color: C.ausente }}>✕</span>
              <span>Ausente (X roja)</span>
            </div>
            <div className="leyenda-item">
              <span className="leyenda-circle-sm" style={{ borderColor: C.corona }} />
              <span>Corona</span>
            </div>
            <div className="leyenda-item">
              <span className="leyenda-dot" style={{ background: C.caries }} />
              <span>Caries</span>
            </div>
            <div className="leyenda-item">
              <span className="leyenda-dot" style={{ background: C.restaurado }} />
              <span>Restaurado</span>
            </div>
            <div className="leyenda-item">
              <span className="leyenda-dot" style={{ background: C.endodoncia }} />
              <span>Endodoncia</span>
            </div>
            <div className="leyenda-item">
              <span className="leyenda-dot" style={{ background: C.implante }} />
              <span>Implante</span>
            </div>
            <div className="leyenda-item">
              <span className="leyenda-dot" style={{ background: C.sellador }} />
              <span>Sellador</span>
            </div>
            <div className="leyenda-item">
              <span className="leyenda-bridge-bar" />
              <span>Puente (pilar / póntico)</span>
            </div>
          </div>
        </details>

        {/* Observaciones */}
        <textarea
          className="odonto-observaciones"
          placeholder="Observaciones del odontograma…"
          value={observaciones}
          onChange={(e) => { setObservaciones(e.target.value); setDirty(true); }}
          rows={3}
        />

        {/* Meta */}
        <div className="odonto-meta pp-info">
          <span>Marcas activas: {summary}</span>
          {metadata?.updated_at && <span> · Última modificación: {metadata.updated_at}</span>}
          {metadata?.profesional && <span> · Profesional: {metadata.profesional}</span>}
          {dirty && <span className="odonto-dirty"> · Cambios sin guardar</span>}
        </div>

        {error && <p className="odonto-error">{error}</p>}

        {/* Acciones */}
        <div className="odonto-buttons">
          <button
            type="button"
            className="boton-secundario"
            onClick={() => {
              if (window.confirm("¿Limpiar todas las marcas del odontograma?")) {
                setDientes(createInitialState());
                setDirty(true);
              }
            }}
          >
            Limpiar todo
          </button>
          <button type="button" className="boton-secundario" onClick={loadOdontograma} disabled={loading}>
            Recargar
          </button>
          {!embedded && (
            <button type="button" className="boton-secundario" onClick={onClose}>
              Cerrar
            </button>
          )}
          <button type="button" className="pp-btn-guardar" onClick={handleSave} disabled={saving}>
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>

      </div>
    </div>
  );
}
